import { overlay } from "overlay-kit";
import { useRef } from "react";
import type { FC, MouseEvent, Ref } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { userHasGlobalAuthority } from "../../../../../shared/capabilities";
import { CourseRole, OrgRole } from "../../../../../shared/types/roles";

import { ManageCourseAdminModal } from "./ManageCourseAdminModal";
import { getDisplayOrgRole, getIconFromOrgRole } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { serverManageCourseAdmin, serverRequestResetPassword } from "api/api-server-actions";
import { showManageEnrollmentModal } from "components/menus/ManageEnrollmentModal";
import { OrgUserType, UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import {
  Menu,
  MenuDivider,
  MenuInstance,
  MenuItem,
  MenuRadioGroup,
  SubMenu,
} from "mds/components/Menu";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  CircleDisabledIcon,
  CopySendArrowIcon,
  DialSettingsIcon,
  DotMenuVerticalIcon,
  LetterIcon,
  PencilEditIcon,
  PersonSingleIcon,
  TextBooksIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { courseGroupingUserSelectors, courseUserSelectors } from "store/selectors";
import { computedStateActions } from "store/slices/computed";
import {
  toastErrorMessage,
  toastLocalizedOperationCatcher,
  toastSuccessMessage,
} from "utils/alerts";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

const ORG_ROLES = [OrgRole.ADMIN, CourseRole.COURSE_ADMIN, null] as const;

export type AdminUserOptionsMenuProps = {
  user: UserType;
  orgUser: OrgUserType;
  inviteStatus: InviteStatus;
  onEdit?: () => void;
};

const manageCourseAdmin = (user: UserType) => {
  return overlay.open(({ isOpen, unmount }) => (
    <Dialog
      open={isOpen}
      title={t("user_options_menu.manage_course_admin.title")}
      onClose={unmount}
    >
      <ManageCourseAdminModal userId={user.id} userName={getUserFullName(user)} onClose={unmount} />
    </Dialog>
  ));
};

export const AdminUserOptionsMenu: FC<AdminUserOptionsMenuProps> = ({
  user,
  orgUser,
  inviteStatus,
  onEdit,
}) => {
  const menuRef: Ref<MenuInstance | null> = useRef(null);
  const dispatch = useAppDispatch();
  const { canImpersonateUser } = useCapabilities();
  const [, copyToClipboard] = useCopyToClipboard();
  const { email, id: userId } = user;
  const courseUsers = useAppSelector((state) => courseUserSelectors.filterByUserId(state, userId));
  const courseGroupingUsers = useAppSelector((state) =>
    courseGroupingUserSelectors.filterByUserId(state, userId),
  );

  const isDisabled = orgUser.is_disabled;
  const isCourseAdmin = courseUsers.length > 0 || courseGroupingUsers.length > 0;
  const currentRole = getDisplayOrgRole(user, orgUser, isCourseAdmin);

  const handleSendInvite = async () => {
    try {
      // We send the invite instead of the password reset if the user has not accepted the eula
      await serverRequestResetPassword(email);

      // Update the user's invite status in the computed slice to reflect the pending state
      dispatch(
        computedStateActions.saveUserInviteStatuses({
          [userId]: InviteStatus.PENDING,
        }),
      );

      toastSuccessMessage(t("user_options_menu.send_invite_success"));
    } catch (error) {
      console.error("Failed to send invite:", error);
      toastErrorMessage(t("user_options_menu.send_invite_error"));
    }
  };

  const onChangeRole = async (newRole: (typeof ORG_ROLES)[number]) => {
    if (newRole === CourseRole.COURSE_ADMIN) {
      manageCourseAdmin(user);
      return;
    }

    if (isCourseAdmin) {
      await serverManageCourseAdmin(userId, []);
      courseUsers.forEach((cu) => dispatch(slices.course_users.actions.removeOne({ id: cu.id })));
      courseGroupingUsers.forEach((cgu) =>
        dispatch(slices.course_grouping_users.actions.removeOne({ id: cgu.id })),
      );
    }

    storeApi.org_users
      .partial_update({
        id: orgUser.id,
        role: newRole,
      })
      .catch(toastLocalizedOperationCatcher("update_user_role"));
  };

  const onImpersonate = async () => {
    const url = await createImpersonationUrl(userId);

    if (url) {
      copyToClipboard(url);
    } else {
      toastErrorMessage(t("error.toasts.failed_to_impersonate_user"));
    }
  };

  const onToggleDisabled = () => {
    storeApi.org_users.partial_update({
      id: orgUser.id,
      is_disabled: !orgUser.is_disabled,
    });
  };

  const MenuButton = (
    <SquareIconButton
      aria-label={t("common.options")}
      kind="ghost"
      size="xs"
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </SquareIconButton>
  );

  return (
    <Menu
      direction="bottom"
      instanceRef={menuRef}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      {email && (
        <>
          <MenuItem
            className="h-auto"
            eventName="Org User Options - Send email"
            eventProps={{ userEmail: email, userId }}
            href={`mailto:${email}`}
            target="_blank"
            allowPropagation
          >
            <div className="flex flex-col gap-1">
              <IconText iconStart={<LetterIcon />} text={t("user_options_menu.email_user")} />
              <div className="ml-6 block">{email}</div>
            </div>
          </MenuItem>

          {inviteStatus !== InviteStatus.ACCEPTED && !isDisabled && (
            <MenuItem
              className="h-auto"
              eventName="Org User Options - Send invite"
              eventProps={{ userEmail: email, userId }}
              onClick={handleSendInvite}
            >
              <div className="flex flex-col gap-1">
                <IconText
                  iconStart={<CopySendArrowIcon />}
                  text={
                    inviteStatus === InviteStatus.NOT_SENT
                      ? t("user_options_menu.send_invite")
                      : t("user_options_menu.resend_invite")
                  }
                />
                <div className="ml-6 block">{email}</div>
              </div>
            </MenuItem>
          )}
        </>
      )}

      {canImpersonateUser && (
        <MenuItem
          eventName="Org User Options - Impersonate user"
          eventProps={{ userEmail: email, userId }}
          onClick={onImpersonate}
        >
          <IconText
            iconStart={<PersonSingleIcon />}
            text={t("user_options_menu.impersonate_user")}
          />
        </MenuItem>
      )}

      <MenuItem onClick={() => showManageEnrollmentModal({ userId })}>
        <IconText
          iconStart={<TextBooksIcon />}
          text={t("user_options_menu.manage_enrollment.menu_label")}
        />
      </MenuItem>

      <MenuItem
        className="justify-between"
        disabled={userHasGlobalAuthority(user)}
        eventName="Org User Options - Edit user information"
        eventProps={{ userEmail: email, userId }}
        onClick={onEdit}
      >
        <IconText
          iconStart={<PencilEditIcon />}
          text={t("user_options_menu.edit_user_info.menu_label")}
        />
        {userHasGlobalAuthority(user) && (
          <TooltipTrigger content={t("user_options_menu.edit_user_info.super_admin_disabled")} />
        )}
      </MenuItem>

      <SubMenu
        disabled={userHasGlobalAuthority(user)}
        label={
          <div className="flex w-full items-center justify-between gap-1">
            <IconText
              iconStart={getIconFromOrgRole(currentRole)}
              text={t("user_options_menu.edit_role")}
            />
            {userHasGlobalAuthority(user) && (
              <TooltipTrigger
                content={t("user_options_menu.edit_user_info.super_admin_disabled")}
              />
            )}
          </div>
        }
        position="auto"
      >
        <MenuRadioGroup
          value={currentRole}
          onRadioChange={(e) => onChangeRole(e.value as (typeof ORG_ROLES)[number])}
        >
          {ORG_ROLES.map((role) => (
            <MenuItem
              eventName="Org User Options - Change user role"
              eventProps={{ userEmail: email, userId }}
              key={role}
              type="radio"
              value={role}
            >
              <IconText
                iconStart={getIconFromOrgRole(role)}
                text={t(`roles.org_${role ?? "other"}`)}
              />
            </MenuItem>
          ))}
        </MenuRadioGroup>

        {currentRole === CourseRole.COURSE_ADMIN && (
          <>
            <MenuDivider />
            <MenuItem
              eventName="Org User Options - Manage course admin access"
              eventProps={{ userEmail: email, userId }}
              onClick={() => onChangeRole(CourseRole.COURSE_ADMIN)}
            >
              <IconText
                iconStart={<DialSettingsIcon />}
                text={t("user_options_menu.manage_course_admin.title")}
              />
            </MenuItem>
          </>
        )}
      </SubMenu>

      {isDisabled ? (
        <MenuItem
          eventName="Org User Options - Enable org user"
          eventProps={{ userEmail: email, userId }}
          onClick={onToggleDisabled}
        >
          <IconText iconStart={<PersonSingleIcon />} text={t("user_actions.enable")} />
        </MenuItem>
      ) : (
        <MenuItem
          eventName="Org User Options - Disable org user"
          eventProps={{ userEmail: email, userId }}
          onClick={onToggleDisabled}
        >
          <IconText
            iconStart={<CircleDisabledIcon className="text-red" />}
            text={t("user_actions.disable")}
          />
        </MenuItem>
      )}
    </Menu>
  );
};
